import { Search } from '@mui/icons-material';
import {
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '@/components/Button';
import { useAlertsStore } from '@/configs/store';
import { useLocalStore } from '@/hooks/useLocalStore';
import { trackerUrls } from '@/tracker/config/urls';
import { Loader } from '@/tracker/ui/Loader';
import { LoadMoreTrigger } from '@/tracker/ui/LoadMoreTrigger';

import { QueuesStore } from './model/QueuesStore';

import s from './QueuesPage.module.scss';

const QueuesPage = observer(() => {
  const alertsStore = useAlertsStore();
  const navigate = useNavigate();
  const { store } = useLocalStore(() => new QueuesStore({ alertsStore }));

  useEffect(() => {
    store.load();
  }, [store]);

  const isEmpty = store.queues.length === 0;

  return (
    <div className={s.page}>
      <div className={s.page__header}>
        <Typography variant="h5" fontWeight="bold">
          Очереди
        </Typography>
        <TextField
          size="small"
          sx={{ width: 320 }}
          placeholder="Ключ или название"
          value={store.query}
          onChange={(event) => store.setQuery(event.target.value)}
          slotProps={{
            htmlInput: { 'aria-label': 'Поиск очередей' },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </div>

      {isEmpty && !store.isLoaded && !store.isError && <Loader />}

      {store.isError && isEmpty && (
        <div className={s.page__empty}>
          <Typography color="text.secondary">Не удалось загрузить очереди</Typography>
          <Button onClick={store.load}>Повторить</Button>
        </div>
      )}

      {store.isLoaded && isEmpty && (
        <div className={s.page__empty}>
          <Typography color="text.secondary">
            {store.query ? 'Ничего не нашлось' : 'Очередей пока нет'}
          </Typography>
        </div>
      )}

      {!isEmpty && (
        <>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Ключ</TableCell>
                <TableCell>Название</TableCell>
                <TableCell>Руководитель</TableCell>
                <TableCell>Доступ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {store.queues.map((queue) => (
                <TableRow
                  key={queue.key}
                  hover
                  className={s.row}
                  onClick={() => navigate(trackerUrls.queue(queue.key))}
                >
                  <TableCell className={s.row__key}>
                    <Link to={trackerUrls.queue(queue.key)} onClick={(e) => e.stopPropagation()}>
                      {queue.key}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <div>{queue.name}</div>
                    {queue.description && (
                      <div className={s.row__description}>{queue.description}</div>
                    )}
                  </TableCell>
                  <TableCell>{queue.lead?.displayName ?? '—'}</TableCell>
                  <TableCell className={s.row__secondary}>
                    {queue.isPublic ? 'Публичная' : 'Участники'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className={s.page__footer}>
            <Typography variant="body2" color="text.secondary">
              Показано {store.queues.length} из {store.totalSize}
            </Typography>
            <LoadMoreTrigger
              hasMore={store.hasMore}
              isLoading={store.isLoading}
              isError={store.isError}
              onLoadMore={store.loadMore}
            />
          </div>
        </>
      )}
    </div>
  );
});

export { QueuesPage };
